import React, { useState, useEffect } from 'react';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import {
  getOrdersByClientByDate, getOrdersByIndividualByDate,
} from './queries';
import OrderCard from './OrderCard';
import { asyncListAll } from 'utilities/graph';
import cache from 'utilities/cache';
import DateRangeSelector from 'components/DateRangeSelector';
import moment from 'moment-timezone';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import { sortBy } from 'utilities/sorting';

const TABS_CONFIG = [
  { label: '集單中', statusFilter: 'pending' },
  { label: '已成單', statusFilter: 'accepted' },
  { label: '已拒單', statusFilter: 'abandoned' },
  { label: '已取消', statusFilter: 'clientCancelled' },
];

const useStyles = makeStyles((theme) => ({
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  tab: {
    '& .MuiTab-wrapper': {
      fontSize: '1.5rem',
      textTransform: 'none', // Prevent ALL CAPS
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px 4px 0 0', // Rounded top corners for horizontal tabs
    },
  },
}));

export default function MyOrders() {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now());

  useEffect(() => {
    const lastWeek = moment().tz(TIME_ZONE).add(-7, 'days').format('YYYY-MM-DD');
    const nextWeek = moment().tz(TIME_ZONE).add(7, 'days').format('YYYY-MM-DD');
    setStartDate(lastWeek);
    setEndDate(nextWeek);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      (async () => {
        setIsLoading(true);
        const clientId = cache.get('app:clientId');
        const [
          currentOrders,
          oldOrders,
        ] = await Promise.all([
          asyncListAll(getOrdersByIndividualByDate, {
            individualId: clientId,
            date: {
              between: [startDate, endDate],
            },
            filter: {
              groupPurchaseStatus: { eq: TABS_CONFIG[selectedTabIndex].statusFilter },
            },
          }),
          asyncListAll(getOrdersByClientByDate, {
            clientId,
            date: {
              between: [startDate, endDate],
            },
            filter: {
              groupPurchaseStatus: { eq: TABS_CONFIG[selectedTabIndex].statusFilter },
            },
          }),
        ]);

        const ordersData = currentOrders.concat(oldOrders).sort(sortBy('deliveryBy'));

        global.logger.debug(ordersData);
        setOrders(ordersData.reverse());
        setIsLoading(false);
      })();
    }
  }, [startDate, endDate, lastUpdatedAt, selectedTabIndex]);

  const handleTabChange = (_event, newIndex) => {
    if (newIndex == selectedTabIndex) return;
    setOrders([]);
    setSelectedTabIndex(newIndex);
  };

  const setCustomPeriod = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setLastUpdatedAt(Date.now());
  };

  const filteredOrders = orders.filter((order) => {
    if (!order) {
      return false;
    }

    const targetStatus = TABS_CONFIG[selectedTabIndex].statusFilter;

    const actualStatus = order.groupPurchaseStatus;

    if (actualStatus === targetStatus) {
      return true;
    }

    return false;
  });

  const onUpdateOrder = (orderId, updateData) => {
    setOrders((orders) => orders.map((order) => (order.id === orderId ? { ...order, ...updateData } : order)));
    if (updateData.groupPurchaseStatus === 'clientCancelled') {
      setSelectedTabIndex(TABS_CONFIG.findIndex((tab) => tab.statusFilter === 'clientCancelled'));
    }
  };

  return (
    <Container maxWidth={false} style={{ padding: 16 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" component="h1" style={{ flex: 1, paddingLeft: 8 }}>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onUpdate={setCustomPeriod}
              alwaysDirty={true}
              isLoading={isLoading}
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.tabsContainer}>
            <Tabs
              value={selectedTabIndex}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {TABS_CONFIG.map((tabConfig, index) => (
                <Tab
                  key={index}
                  label={tabConfig.label}
                  className={classes.tab}
                  id={`order-tab-${index}`}
                  aria-controls={`order-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Box>
        </Grid>
        {filteredOrders.map((order) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
            <OrderCard order={order} updateOrder={onUpdateOrder} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

MyOrders.propTypes = {};
